import { SearchRounded } from "@mui/icons-material";
import { Box, InputAdornment, TextField } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useEffect, useMemo, useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import ExportButton from "../../../../../../components/buttons/ExportButton";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import StyledDataGrid from "../../../../../../components/StyledDataGrid";
import { useFileNaming } from "../../../../../../hooks/useFileNaming";
import { ComplexEntityTypes } from "../../../../../../utils/constants";
import { getPrettyDateTime } from "../../../../../../utils/timestamp";
import { useEntities } from "../../../hooks/useEntities";
import EntityDrawer from "../../EntityDrawer/EntityDrawer";
import TextIntegrationStyle from "../../TextIntegrationStyle";

const DataStoreActivityView = ({ entityData }) => {
  const { useQueryForEntitiesByType } = useEntities();
  const { trackEvent } = useAPI();
  const colors = useColors();
  const { generateBreezeFileName } = useFileNaming();
  const gridRef = useGridApiRef();
  const { data: userEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.USER,
  });
  const { data: nhiEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.NONHUMAN_IDENTITY,
  });

  const [isEntityDrawerOpen, setIsEntityDrawerOpen] = useState(false);
  const [clickedEntity, setClickedEntity] = useState(null);
  const [filteredAccessLogs, setFilteredAccessLogs] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [sortModel, setSortModel] = useState([
    {
      field: "eventTime",
      sort: "asc",
    },
  ]);

  const getAccessLogEventRows = (entityData, userEntities, nhiEntities) => {
    const rowsMap = [];
    if (!entityData?.aws_data?.access_logs?.length) {
      return rowsMap;
    }

    entityData.aws_data.access_logs.forEach((accessLog, index) => {
      const user = accessLog.user;
      const eventType = accessLog.event_type;
      const eventTime = accessLog.event_time;

      // Find corresponding user or NHI entity
      let entityObj = null;
      let entityType = null;

      // First try to find in user entities
      if (userEntities) {
        entityObj = userEntities.find(
          (entity) =>
            entity.unique_id === user ||
            entity.original_unique_id === user ||
            entity.aws_data?.user_name === user ||
            entity.display_name === user
        );
        if (entityObj) {
          entityType = "user";
        }
      }

      // If not found in users, try NHI entities
      if (!entityObj && nhiEntities) {
        entityObj = nhiEntities.find(
          (entity) =>
            entity.unique_id === user ||
            entity.original_unique_id === user ||
            entity.aws_data?.role_name === user ||
            entity.display_name === user
        );
        if (entityObj) {
          entityType = "nhi";
        }
      }

      rowsMap.push({
        id: `${entityData.entity_id}_${index}`,
        user,
        eventType,
        eventTime,
        entityObj,
        entityType,
        displayName: entityObj?.display_name || user,
        originalUniqueId: entityObj?.original_unique_id || user,
      });
    });

    return rowsMap;
  };

  const allAccessLogEvents = useMemo(() => {
    return getAccessLogEventRows(entityData, userEntities, nhiEntities);
  }, [entityData, userEntities, nhiEntities]);

  useEffect(() => {
    if (searchString !== "") {
      const lowerCaseSearchString = searchString.toLowerCase();
      const filteredRows = allAccessLogEvents.filter(
        (row) =>
          row.eventType?.toLowerCase().includes(lowerCaseSearchString) ||
          row.user?.toLowerCase().includes(lowerCaseSearchString)
      );
      setFilteredAccessLogs(filteredRows);
    } else {
      setFilteredAccessLogs(allAccessLogEvents);
    }
  }, [allAccessLogEvents, searchString]);

  const onAccessLogClick = (params) => {
    if (params?.row?.entityObj) {
      trackEvent(
        `User clicked on access log event for user ${params?.row?.user}, from datastore access logs view ${entityData?.display_name}`
      );
      setIsEntityDrawerOpen(true);
      setClickedEntity(params.row.entityObj);
    }
  };

  const accessLogColumnsToShow = useMemo(
    () => [
      {
        field: "eventTime",
        headerName: "Event Time",
        flex: 0.25,
        valueGetter: (params) => {
          const eventTime = params?.row?.eventTime?.timestamp_utc;
          return eventTime ? getPrettyDateTime(eventTime) : "";
        },
        renderCell: (params) => {
          const eventTime = params?.row?.eventTime?.timestamp_utc;
          return (
            <Box>
              <TextIntegrationStyle
                title={""}
                value={eventTime ? getPrettyDateTime(eventTime) : "-"}
              />
            </Box>
          );
        },
      },
      {
        field: "eventType",
        headerName: "Event Type",
        flex: 0.3,
        valueGetter: (params) => params?.row?.eventType || "",
        renderCell: (params) => (
          <Box>
            <TextIntegrationStyle
              title={""}
              value={params?.row?.eventType || "-"}
            />
          </Box>
        ),
      },
      {
        field: "user",
        headerName: "User",
        flex: 0.45,
        valueGetter: (params) => params?.row?.user || "",
        renderCell: (params) => {
          const hasEntity = params?.row?.entityObj;
          const user = params?.row?.user || "-";

          return (
            <CustomToolTip
              title={
                hasEntity ? "Click to view entity details" : "Entity not found"
              }
            >
              <Box
                sx={{
                  color: hasEntity ? "primary.main" : "inherit",
                  textDecoration: hasEntity ? "underline" : "none",
                  cursor: hasEntity ? "pointer" : "default",
                  "&:hover": hasEntity
                    ? {
                        color: "primary.dark",
                      }
                    : {},
                }}
              >
                <TextIntegrationStyle title={""} value={user} />
              </Box>
            </CustomToolTip>
          );
        },
      },
    ],
    []
  );

  const handleTableExport = () => {
    trackEvent(
      `User clicked export button for datastore access logs - ${entityData?.display_name}`
    );
    const datastoreName =
      entityData?.display_name?.replace(/[^a-zA-Z0-9]/g, "_") || "datastore";
    const csvExportOptions = {
      fileName: generateBreezeFileName(`${datastoreName}_access_logs`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <TextField
          InputProps={{
            sx: {
              color: colors.contrast_text_dark,
              fontSize: "14px",
            },
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded
                  style={{
                    color: colors.text_disabled,
                    width: "16px",
                    height: "16px",
                  }}
                />
              </InputAdornment>
            ),
          }}
          placeholder="Search"
          type="text"
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "8px",
            },
            "& .MuiInputBase-root": { height: "32px" },
          }}
        />
        <ExportButton
          handleExport={handleTableExport}
          disabled={!filteredAccessLogs || filteredAccessLogs.length === 0}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <StyledDataGrid
          columns={accessLogColumnsToShow}
          rows={filteredAccessLogs}
          onRowClick={onAccessLogClick}
          getRowClassName={(params) =>
            params.row?.entityObj ? "clickable-row" : "non-clickable-row"
          }
          sx={{
            "& .clickable-row": {
              cursor: "pointer",
            },
            "& .non-clickable-row": {
              cursor: "default",
            },
          }}
          apiRef={gridRef}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
        />
      </Box>

      <EntityDrawer
        openDrawer={isEntityDrawerOpen}
        selectedEntity={clickedEntity}
        onClose={() => setIsEntityDrawerOpen(false)}
      />
    </>
  );
};
export default DataStoreActivityView;
